import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  buildMessagesSearch,
  getChatUserIdFromSearch,
  normalizeChatUserId,
} from "./navigationState";

const MESSAGES_BACKGROUND_STORAGE_KEY = "messages-page-background-mode";

function getInitialMessagesBackgroundMode() {
  if (typeof window === "undefined") return "video";
  const savedMode = window.localStorage.getItem(MESSAGES_BACKGROUND_STORAGE_KEY);
  return savedMode === "white" ? "white" : "video";
}

function getIsMobileMessagesViewport() {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= 768;
}

export default function useMessagingUiState() {
  const [showInbox, setShowInbox] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [chatUserIdState, setChatUserIdState] = useState(() => {
    if (location.pathname !== "/messages") {
      return null;
    }

    return getChatUserIdFromSearch(location.search) || null;
  });
  const [pendingChatText, setPendingChatText] = useState("");
  const [pendingChatUserName, setPendingChatUserName] = useState("");
  const [showMessagesSettings, setShowMessagesSettings] = useState(false);
  const [messagesBackgroundMode, setMessagesBackgroundMode] = useState(
    getInitialMessagesBackgroundMode
  );
  const [isMobileMessagesViewport, setIsMobileMessagesViewport] = useState(
    getIsMobileMessagesViewport
  );
  const messagesSettingsRef = useRef(null);
  const pendingConversationNavigationRef = useRef(null);
  const chatUserId =
    location.pathname === "/messages"
      ? getChatUserIdFromSearch(location.search) || null
      : chatUserIdState;

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      MESSAGES_BACKGROUND_STORAGE_KEY,
      messagesBackgroundMode
    );
  }, [messagesBackgroundMode]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setIsMobileMessagesViewport(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!showMessagesSettings) return;

    const handlePointerDownOutsideMessagesSettings = (event) => {
      if (!messagesSettingsRef.current?.contains(event.target)) {
        setShowMessagesSettings(false);
      }
    };

    document.addEventListener(
      "pointerdown",
      handlePointerDownOutsideMessagesSettings
    );
    return () => {
      document.removeEventListener(
        "pointerdown",
        handlePointerDownOutsideMessagesSettings
      );
    };
  }, [showMessagesSettings]);

  useEffect(() => {
    if (location.pathname !== "/messages") {
      return;
    }

    const nextChatUserId = getChatUserIdFromSearch(location.search) || null;
    setChatUserIdState((currentChatUserId) =>
      currentChatUserId === nextChatUserId ? currentChatUserId : nextChatUserId
    );
  }, [location.pathname, location.search]);

  useEffect(() => {
    return () => {
      if (pendingConversationNavigationRef.current) {
        window.clearTimeout(pendingConversationNavigationRef.current);
      }
    };
  }, []);

  const openConversation = useCallback(
    (nextUserId, text = "", userName = "") => {
      const normalizedChatUserId = normalizeChatUserId(nextUserId);
      if (!normalizedChatUserId) {
        return;
      }

      if (pendingConversationNavigationRef.current) {
        window.clearTimeout(pendingConversationNavigationRef.current);
        pendingConversationNavigationRef.current = null;
      }

      setChatUserIdState(normalizedChatUserId);
      setPendingChatText(text || "");
      setPendingChatUserName(userName || "");

      const conversationTarget = {
        pathname: "/messages",
        search: buildMessagesSearch("", normalizedChatUserId),
        hash: "",
      };

      if (location.pathname === "/messages") {
        navigate(conversationTarget, {
          state: {
            ...(location.state || {}),
            messagesView: "conversation",
          },
        });
        return;
      }

      navigate(
        {
          pathname: "/messages",
          search: buildMessagesSearch("", ""),
          hash: "",
        },
        {
          state: {
            ...(location.state || {}),
            messagesView: "inbox",
          },
        }
      );

      pendingConversationNavigationRef.current = window.setTimeout(() => {
        pendingConversationNavigationRef.current = null;
        navigate(conversationTarget, {
          state: {
            ...(location.state || {}),
            messagesView: "conversation",
          },
        });
      }, 0);
    },
    [location.pathname, location.state, navigate]
  );

  useEffect(() => {
    const openChatHandler = (event) => {
      const { userId, text, userName } = event.detail || {};
      openConversation(userId, text || "", userName || "");
    };

    window.addEventListener("openChatWith", openChatHandler);
    window.addEventListener("sharePostAsMessage", openChatHandler);
    return () => {
      window.removeEventListener("openChatWith", openChatHandler);
      window.removeEventListener("sharePostAsMessage", openChatHandler);
    };
  }, [openConversation]);

  const setChatUserId = useCallback(
    (nextValue) => {
      const resolvedValue =
        typeof nextValue === "function" ? nextValue(chatUserId) : nextValue;
      const nextChatUserId = normalizeChatUserId(
        resolvedValue && typeof resolvedValue === "object"
          ? resolvedValue.userId
          : resolvedValue
      );
      const nextChatUserName =
        resolvedValue && typeof resolvedValue === "object"
          ? resolvedValue.userName || ""
          : "";

      if (!nextChatUserId) {
        setChatUserIdState(null);
        setPendingChatText("");
        setPendingChatUserName("");

        navigate(
          {
            pathname: "/messages",
            search: buildMessagesSearch(location.search, ""),
            hash: "",
          },
          { replace: true }
        );
        return;
      }

      openConversation(nextChatUserId, "", nextChatUserName);
    },
    [chatUserId, location.search, navigate, openConversation]
  );

  const handleCloseChat = () => {
    if (pendingConversationNavigationRef.current) {
      window.clearTimeout(pendingConversationNavigationRef.current);
      pendingConversationNavigationRef.current = null;
    }

    setChatUserIdState(null);
    setPendingChatText("");
    setShowMessagesSettings(false);

    if (location.pathname !== "/messages" || !chatUserId) {
      return;
    }

    if (location.state?.messagesView === "conversation") {
      navigate(-1);
      return;
    }

    navigate(
      {
        pathname: "/messages",
        search: buildMessagesSearch(location.search, ""),
        hash: "",
      },
      { replace: true }
    );
  };

  const handleOpenInbox = () => {
    if (pendingConversationNavigationRef.current) {
      window.clearTimeout(pendingConversationNavigationRef.current);
      pendingConversationNavigationRef.current = null;
    }

    setShowInbox(true);
    setChatUserIdState(null);
    setPendingChatText("");
    setPendingChatUserName("");
    setShowMessagesSettings(false);

    if (location.pathname === "/messages" && chatUserId) {
      navigate(
        {
          pathname: "/messages",
          search: buildMessagesSearch(location.search, ""),
          hash: "",
        },
        { replace: true }
      );
    }
  };

  return {
    showInbox,
    setShowInbox,
    chatUserId,
    setChatUserId,
    pendingChatText,
    pendingChatUserName,
    setPendingChatText,
    showMessagesSettings,
    setShowMessagesSettings,
    messagesBackgroundMode,
    setMessagesBackgroundMode,
    isMobileMessagesViewport,
    messagesSettingsRef,
    handleCloseChat,
    handleOpenInbox,
  };
}
